import React, { useState } from 'react';
import { Plus, Check, X } from 'lucide-react';
import { API } from '../../App';

/**
 * AddVersionInline — bottom-of-FilesPanel button that creates a
 * placeholder fray for a past version (v1, v2, etc) without forcing
 * a run. POSTs to /api/frays/import-session with empty session_md
 * (backend creates a label-only placeholder per #1131 Task 1.1).
 */
export default function AddVersionInline({ projectId, onAdded }) {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const close = () => {
    setOpen(false);
    setLabel('');
    setError(null);
  };

  const submit = async () => {
    const trimmed = label.trim();
    if (!trimmed) return;
    setSaving(true);
    setError(null);
    try {
      await API.post('/api/frays/import-session', {
        label: trimmed,
        project_id: projectId,
        session_md: '',
      });
      close();
      if (onAdded) onAdded();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to add version');
    }
    setSaving(false);
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="add-version-toggle"
        className="w-full mt-2 flex items-center justify-center gap-2 px-4 py-2 bg-[#0F0F0F] border border-dashed border-[#2A2A2A] hover:border-[#FF4D00] rounded text-xs font-mono uppercase tracking-wider text-[#A0A0A0] hover:text-[#F5F1E8] transition-colors"
      >
        <Plus className="w-3.5 h-3.5" />
        Add Version
      </button>
    );
  }

  return (
    <div className="mt-2 flex items-center gap-2 p-2 bg-[#1E1E1E] border border-[#FF4D00] rounded">
      <input
        type="text"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') close();
          if (e.key === 'Enter') submit();
        }}
        autoFocus
        placeholder="Label (e.g. v1, v2)"
        data-testid="add-version-input"
        className="flex-1 px-3 py-1.5 bg-[#0F0F0F] border border-[#2A2A2A] rounded text-xs font-mono text-[#F5F1E8] focus:border-[#FF4D00] focus:outline-none"
      />
      <button
        type="button"
        onClick={submit}
        disabled={saving || !label.trim()}
        data-testid="add-version-submit"
        className="p-1.5 text-[#10B981] disabled:opacity-50"
        title="Create version"
      >
        <Check className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={close}
        disabled={saving}
        data-testid="add-version-cancel"
        className="p-1.5 text-[#A0A0A0] hover:text-[#F5F1E8] disabled:opacity-50"
        title="Cancel"
      >
        <X className="w-4 h-4" />
      </button>
      {error && <span className="text-[10px] font-mono text-[#A61E17]">{error}</span>}
    </div>
  );
}
